import { useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { isOrphan } from '@backstage/plugin-catalog';
import {
  Box,
  Button,
  Collapse,
  Typography,
  makeStyles,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/ReportProblemOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useComponentPage } from './ComponentPageContext';

export const EDIT_URL = 'backstage.io/edit-url';

const useStyles = makeStyles(theme => ({
  banner: {
    border: '1px solid rgba(219, 171, 10, 0.55)',
    background: 'rgba(253, 229, 73, 0.14)',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3),
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  icon: { color: 'var(--ui-warn-border)' },
  summary: { flex: 1, minWidth: 200 },
  details: { marginTop: theme.spacing(1), paddingLeft: theme.spacing(4) },
  detail: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8rem',
    overflowWrap: 'anywhere',
  },
}));

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/** What the banner lists, in words — each defect the catalog reports. */
export function defectsOf(
  entity: Entity,
  unresolved: string[],
  processingErrors: { on: string; message: string }[],
) {
  const parts: string[] = [];
  if (isOrphan(entity)) parts.push('no longer in its catalog file');
  if (unresolved.length) {
    parts.push(
      `${plural(
        unresolved.length,
        'relation',
        'relations',
      )} could not be resolved`,
    );
  }
  if (processingErrors.length) {
    parts.push(
      plural(processingErrors.length, 'processing error', 'processing errors'),
    );
  }
  return parts;
}

/**
 * One banner for every catalog defect of a component — orphaned, naming an
 * entity that does not exist, or failing to be read — on every tab of its page
 * (specs/component-page.md). Three separate warnings used to stack above the
 * Overview only; a defect is the page's, not one tab's.
 *
 * No defect, no banner: there is no all-clear.
 */
export const CatalogDefectsBanner = () => {
  const classes = useStyles();
  const { entity, catalog } = useComponentPage();
  const [open, setOpen] = useState(false);
  if (catalog.loading) return null;

  const parts = defectsOf(entity, catalog.unresolved, catalog.processingErrors);
  if (!parts.length) return null;
  const editUrl = entity.metadata.annotations?.[EDIT_URL];

  return (
    // A region rather than an alert: the defect is a lasting state of the
    // entry, and an alert would interrupt a screen reader on every page load.
    <Box className={classes.banner} role="region" aria-label="Catalog defects">
      <div className={classes.head}>
        <WarningIcon className={classes.icon} fontSize="small" />
        <Typography className={classes.summary}>
          <strong>Catalog metadata issue.</strong>{' '}
          {parts.join(' · ').replace(/^./, c => c.toUpperCase())}.
        </Typography>
        {editUrl && (
          <Button
            size="small"
            variant="outlined"
            href={editUrl}
            target="_blank"
            rel="noopener"
          >
            Review metadata
          </Button>
        )}
      </div>
      <Button
        size="small"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        startIcon={
          <ExpandMoreIcon
            style={{ transform: open ? 'rotate(180deg)' : undefined }}
          />
        }
      >
        Technical details
      </Button>
      <Collapse in={open}>
        <div className={classes.details}>
          {isOrphan(entity) && (
            <Typography className={classes.detail}>
              Orphaned: no location in the catalog provides this entity any
              more.
            </Typography>
          )}
          {catalog.unresolved.map(ref => (
            <Typography key={ref} className={classes.detail}>
              Unresolved relation: {ref}
            </Typography>
          ))}
          {catalog.processingErrors.map((e, i) => (
            <Typography key={i} className={classes.detail}>
              Processing error on {e.on}: {e.message}
            </Typography>
          ))}
        </div>
      </Collapse>
    </Box>
  );
};
